import type * as React from 'react'
import { createContext, useContext } from 'react'

import type { InvoiceTransaction, TransactionRow } from '../../../../api/useFreight/types'
import type { CostRevenueType, WithDrawerTriggerProps } from '../types'
import { useCostRevenueSelection, useLaunchpadData, useLaunchpadDrawer } from '../hooks'
import type { CustomerOrder } from '../../../../api/useFreight/types/dashboard'

/**
 * Launchpad context interface
 */
interface LaunchpadContextValue {
  // State values
  costRevenue: CostRevenueType
  setCostRevenue: (value: CostRevenueType) => void
  data: ReturnType<typeof useLaunchpadData>['data']
  isLoading: boolean
  drawerState: {
    visible: boolean
    orderNumber?: string
    orders: TransactionRow[] | InvoiceTransaction[] | CustomerOrder[]
    currentIndex: number
  }
  // Action handlers
  triggerDrawer: WithDrawerTriggerProps['triggerDrawer']
  handleNavigate: (newIndex: number) => void
  closeDrawer: () => void
}

// Create context with a default value
const LaunchpadContext = createContext<LaunchpadContextValue | undefined>(undefined)

/**
 * Provider component for Launchpad context
 */
export function LaunchpadProvider({ children }: { children: React.ReactNode }) {
  const [costRevenue, setCostRevenue] = useCostRevenueSelection()
  const { data, isLoading } = useLaunchpadData(costRevenue)
  const [drawerState, { triggerDrawer, handleNavigate, closeDrawer }] = useLaunchpadDrawer()

  // Context value
  const value: LaunchpadContextValue = {
    costRevenue,
    setCostRevenue,
    data,
    isLoading,
    drawerState,
    triggerDrawer,
    handleNavigate,
    closeDrawer,
  }

  return <LaunchpadContext.Provider value={value}>{children}</LaunchpadContext.Provider>
}

/**
 * Custom hook to use the Launchpad context
 */
export function useLaunchpadContext() {
  const context = useContext(LaunchpadContext)
  if (context === undefined) {
    throw new Error('useLaunchpadContext must be used within a LaunchpadProvider')
  }
  return context
}
